/**
 * Cart Drawer Guarantee Module
 *
 * Injects guarantee blocks into the Recharge cart drawer by fetching
 * guarantee data from the cart JSON endpoint and updating the shadow DOM.
 */

;(async function CartDrawerGuaranteeModule() {
  'use strict'

  const cartDrawerSelector = '#recharge-storefront-experiences__cart-drawer'
  const guaranteeContainerId = 'injected-guarantees'
  const guaranteeStyleId = 'guarantee-styles'

  /**
   * @typedef {{ text: string, icon?: { url: string } | null }} Guarantee
   * @typedef {{ guarantee?: Guarantee }} CartItem
   * @typedef {{ items: CartItem[] }} CartData
   */

  /** @type {MutationObserver | null} */
  let mutationObserver = null
  /** @type {MutationObserver | null} */
  let shadowRootObserver = null
  /** @type {string | null} */
  let lastProcessedState = null

  /**
   * Initialize the mutation observer
   */
  function init() {
    // Watch for the cart drawer element to appear
    watchForCartDrawer()
  }

  /**
   * Watch for cart drawer element to appear in DOM
   */
  function watchForCartDrawer() {
    const checkCartDrawer = () => {
      const cartDrawerHost = document.querySelector(cartDrawerSelector)
      if (cartDrawerHost?.shadowRoot) {
        initializeShadowObserver(cartDrawerHost.shadowRoot)
        return true
      }
      return false
    }

    // Check immediately
    if (!checkCartDrawer()) {
      // Set up observer for cart drawer host to appear
      mutationObserver = new MutationObserver((_, observer) => {
        if (checkCartDrawer()) {
          observer.disconnect()
        }
      })

      mutationObserver.observe(document.body, {
        childList: true,
        subtree: true
      })
    }
  }

  /**
   * Initialize observer for shadow root changes
   * @param {ShadowRoot} shadowRoot
   */
  function initializeShadowObserver(shadowRoot) {
    if (shadowRootObserver) {
      shadowRootObserver.disconnect()
    }

    // Create debounced update function using a simple debounce pattern
    /** @type {ReturnType<typeof setTimeout> | undefined} */
    let updateTimeout
    const debouncedUpdate = () => {
      clearTimeout(updateTimeout)
      updateTimeout = setTimeout(() => {
        checkAndUpdateGuarantees(shadowRoot)
      }, 300)
    }

    shadowRootObserver = new MutationObserver((mutations) => {
      // Check if drawer state changed or cart items changed
      const hasRelevantChange = mutations.some(mutation => {
        // Check for dialog state changes
        if (mutation.type === 'attributes' &&
            mutation.target instanceof Element &&
            mutation.target.getAttribute('role') === 'dialog' &&
            mutation.attributeName === 'data-state') {
          return true
        }

        // Check for changes in cart items section
        if (mutation.type === 'childList') {
          const target = mutation.target
          if (target instanceof Element) {
            // Check if changes occurred in cart items area
            if (target.getAttribute('role') === 'list' ||
                target.closest('section[role="list"]') ||
                target.querySelector('section[role="list"]')) {
              return true
            }
          }
        }

        return false
      })

      if (hasRelevantChange) {
        debouncedUpdate()
      }
    })

    // Observe the shadow root
    shadowRootObserver.observe(shadowRoot, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state']
    })

    // Initial check
    checkAndUpdateGuarantees(shadowRoot)
  }

  /**
   * Check drawer state and update guarantees if needed
   * @param {ShadowRoot} shadowRoot
   */
  async function checkAndUpdateGuarantees(shadowRoot) {
    if (!isDrawerOpen(shadowRoot)) {
      lastProcessedState = 'closed'
      return
    }

    // Get current cart state signature
    const cartItemsSection = shadowRoot.querySelector('section[role="list"]')
    if (!cartItemsSection) {
      return
    }

    const cartItems = cartItemsSection.querySelectorAll('[role="listitem"]')
    const currentState = `open-${cartItems.length}-${Date.now()}`

    // Only update if state has meaningfully changed
    if (lastProcessedState &&
        lastProcessedState.startsWith('open') &&
        currentState.startsWith(`open-${cartItems.length}`)) {
      // Same number of items, skip update unless enough time has passed
      const lastTime = parseInt(lastProcessedState.split('-')[2] || '0')
      if (Date.now() - lastTime < 1000) {
        return
      }
    }

    lastProcessedState = currentState
    await injectGuarantees()
  }


  /**
   * Get the shadow root of the cart drawer
   * @returns {ShadowRoot|null}
   */
  function getShadowRoot() {
    const cartDrawerHost = document.querySelector(cartDrawerSelector)
    return cartDrawerHost?.shadowRoot || null
  }

  /**
   * Check if the cart drawer is open
   * @param {ShadowRoot} shadowRoot
   * @returns {boolean}
   */
  function isDrawerOpen(shadowRoot) {
    const dialog = shadowRoot.querySelector('[role="dialog"][data-state="open"]')
    return !!dialog
  }

  /**
   * Fetch cart data with guarantee information from JSON endpoint
   * @returns {Promise<CartData|null>}
   */
  async function fetchCartData() {
    try {
      // Add timestamp to prevent caching
      const timestamp = Date.now()
      const response = await fetch(`/cart?view=json&t=${timestamp}`, {
        cache: 'no-cache',
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`)
      }
      return await response.json()
    } catch (error) {
      return null
    }
  }

  /**
   * Extract unique guarantees from cart items
   * @param {CartItem[]} items - Cart items array
   * @returns {Map<string, { content: string, icon: string | null }>} Map of unique guarantees
   */
  function extractUniqueGuarantees(items) {
    const uniqueGuarantees = new Map()

    items.forEach((item) => {
      if (item.guarantee?.text) {
        const guaranteeText = item.guarantee.text
        const guaranteeIcon = item.guarantee.icon?.url || null

        if (!uniqueGuarantees.has(guaranteeText)) {
          uniqueGuarantees.set(guaranteeText, {
            content: guaranteeText,
            icon: guaranteeIcon
          })
        }
      }
    })

    return uniqueGuarantees
  }

  /**
   * Get guarantee styles
   * @returns {string} CSS styles
   */
  function getGuaranteeStyles() {
    return `
      .cart-guarantees {
        margin: 16px 0;
        transition: opacity 0.2s ease;
      }

      .guarantee-block {
        padding: 16px 24px;
        margin-bottom: 8px;
        display: flex;
        align-items: center;
        justify-content: flex-start;
        gap: 8px;
        animation: fadeIn 0.3s ease;
      }

      @keyframes fadeIn {
        from {
          opacity: 0;
          transform: translateY(-5px);
        }
        to {
          opacity: 1;
          transform: translateY(0);
        }
      }

      .guarantee-block:last-child {
        margin-bottom: 0;
      }

      .guarantee-icon {
        width: 62px;
        flex-shrink: 0;
      }

      .guarantee-text {
        color: #333;
        font-size: 13px;
        line-height: 1.4;
      }
    `
  }

  /**
   * Inject styles into shadow DOM
   * @param {ShadowRoot} shadowRoot
   */
  function injectStyles(shadowRoot) {
    if (!shadowRoot.querySelector(`#${guaranteeStyleId}`)) {
      const styleElement = document.createElement('style')
      styleElement.id = guaranteeStyleId
      styleElement.textContent = getGuaranteeStyles()
      shadowRoot.appendChild(styleElement)
    }
  }

  /**
   * Remove all guarantees from the shadow DOM
   * @param {ShadowRoot} shadowRoot
   */
  function removeAllGuarantees(shadowRoot) {
    const guaranteeContainer = shadowRoot.querySelector(`#${guaranteeContainerId}`)
    guaranteeContainer?.remove()
  }

  /**
   * Create a guarantee block element
   * @param {string} content - Guarantee text content
   * @param {string|null} icon - Icon URL
   * @returns {HTMLElement}
   */
  function createGuaranteeBlock(content, icon) {
    const guaranteeDiv = document.createElement('div')
    guaranteeDiv.classList.add('guarantee-block')
    guaranteeDiv.setAttribute('data-guarantee-content', content)

    if (icon) {
      const iconElement = document.createElement('img')
      iconElement.src = icon
      iconElement.alt = 'Guarantee icon'
      iconElement.classList.add('guarantee-icon')
      guaranteeDiv.appendChild(iconElement)
    }

    const textElement = document.createElement('div')
    textElement.classList.add('guarantee-text')
    textElement.innerHTML = content
    guaranteeDiv.appendChild(textElement)

    return guaranteeDiv
  }

  /**
   * Update guarantee display in the shadow DOM
   * @param {ShadowRoot} shadowRoot
   * @param {Map<string, { content: string, icon: string | null }>} uniqueGuarantees - Map of unique guarantees
   * @returns {HTMLElement|null}
   */
  function updateGuaranteeDisplay(shadowRoot, uniqueGuarantees) {
    const cartItemsSection = shadowRoot.querySelector('section[role="list"]')
    if (!cartItemsSection) {
      return null
    }

    let guaranteeContainer = shadowRoot.querySelector(`#${guaranteeContainerId}`)

    if (!guaranteeContainer) {
      guaranteeContainer = document.createElement('div')
      guaranteeContainer.id = guaranteeContainerId
      guaranteeContainer.classList.add('cart-guarantees')
      cartItemsSection.parentNode?.insertBefore(guaranteeContainer, cartItemsSection.nextSibling)
    }

    if (!(guaranteeContainer instanceof HTMLElement)) {
      return null
    }

    // Get existing blocks
    const existingBlocks = new Map()
    guaranteeContainer.querySelectorAll('.guarantee-block').forEach((block) => {
      const content = block.getAttribute('data-guarantee-content')
      if (content) {
        existingBlocks.set(content, block)
      }
    })

    // Remove blocks no longer in cart
    existingBlocks.forEach((block, content) => {
      if (!uniqueGuarantees.has(content)) {
        block.remove()
      }
    })

    // Add new blocks
    uniqueGuarantees.forEach((guarantee, content) => {
      if (!existingBlocks.has(content)) {
        const guaranteeBlock = createGuaranteeBlock(content, guarantee.icon)
        guaranteeContainer.appendChild(guaranteeBlock)
      }
    })

    // If no guarantees remain, remove container
    if (uniqueGuarantees.size === 0) {
      guaranteeContainer.remove()
      return null
    }

    return guaranteeContainer
  }

  /**
   * Inject guarantees into the cart drawer
   * @returns {Promise<boolean>}
   */
  async function injectGuarantees() {
    const shadowRoot = getShadowRoot()
    if (!shadowRoot) {
      return false
    }

    if (!isDrawerOpen(shadowRoot)) {
      return false
    }

    try {
      // Fetch cart data
      const cartData = await fetchCartData()

      // Check if cart is empty
      if (!cartData?.items?.length) {
        removeAllGuarantees(shadowRoot)
        return false
      }

      // Extract unique guarantees
      const uniqueGuarantees = extractUniqueGuarantees(cartData.items)

      // Always inject styles if we have a shadow root
      injectStyles(shadowRoot)

      // Update guarantee display - this handles both adding and removing
      updateGuaranteeDisplay(shadowRoot, uniqueGuarantees)

      return true
    } catch (error) {
      return false
    }
  }

  // Initialize the module when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init)
  } else {
    init()
  }

})()